import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore"
import { firestore } from "../firebase"
import { ITrip, ITripSearch, TripData, TripID, TripSearchID } from "@cruisen/contract"
import { callFirebaseFunction } from "../callFirebaseFunction"
import { SearchDestination, SearchOrigin, TripModel } from "@cruisen/react-shared"
import isEqual from "lodash.isequal"
import { Analytics } from "../../utils/analytics"

const tripsCollection = collection(firestore, "Trips")

export const subscribeTrip = (tripId: string, callback: (trip: Partial<ITrip>) => void) => {
  const tripRef = doc(firestore, "Trips", tripId)

  return onSnapshot(tripRef, (tripDoc) => {
    if (tripDoc.exists()) {
      const tripData = { ...tripDoc.data(), id: tripId } as ITrip
      // Updatable fields
      const {
        name,
        collaboratorIds,
        collaborators,
        inviteLink,
        originPorts,
        originPortGroups,
        destinationPorts,
        destinationRegions,
        twilioConversationSid,
        twilioConversationMessagesCounter,
        twilioConversationLastMessageDate,
        chosenTripSearchResultId,
        isNavigatorLoading,
        months,
        updatedBy,
        cruiseSearchPreferences,
        tags,
      } = tripData

      callback({
        name,
        collaboratorIds,
        collaborators,
        inviteLink,
        originPorts,
        originPortGroups,
        destinationPorts,
        destinationRegions,
        twilioConversationSid,
        twilioConversationMessagesCounter,
        twilioConversationLastMessageDate,
        chosenTripSearchResultId,
        isNavigatorLoading,
        months,
        updatedBy,
        cruiseSearchPreferences,
        tags,
      })
    }
  })
}

export const getTrip = async (tripId: string): Promise<ITrip | null> => {
  console.log("load trip data", { tripId })
  const tripDoc = await getDoc(doc(tripsCollection, tripId))
  if (!tripDoc.exists) {
    console.error("trip not found", { tripId })
    return null
  }
  return {
    twilioConversationMessagesCounter: 0,
    twilioConversationLastMessageDate: 0,
    ...tripDoc.data(),
    id: tripDoc.id,
  } as ITrip
}

export const userTripsQueryKey = (userId: string) => ["userTrips", userId]

export const getUserTrips = async (userId: string, isSuperAdmin?: boolean) => {
  try {
    const tripsQuery = isSuperAdmin
      ? query(tripsCollection)
      : query(tripsCollection, where("collaboratorIds", "array-contains", userId))

    const tripSnapshot = await getDocs(tripsQuery)
    const trips = [] as ITrip[]

    tripSnapshot.forEach((doc) => {
      trips.push({
        twilioConversationMessagesCounter: 0,
        twilioConversationLastMessageDate: 0,
        ...doc.data(),
        id: doc.id,
      } as ITrip)
    })

    const roomsPromises = trips.map(async (trip) => {
      const roomsRef = collection(doc(firestore, "Trips", trip.id), "Rooms")
      const roomsSnapshot = await getDocs(roomsRef)

      return {
        tripId: trip.id,
        rooms: roomsSnapshot.docs.map((room) => ({
          ...room.data(),
          id: room.id,
        })),
      }
    })

    const roomsResults = await Promise.all(roomsPromises)

    const tripsWithRooms = trips.map((trip) => {
      const roomsData = roomsResults.find((r) => r.tripId === trip.id)
      return {
        ...trip,
        rooms: roomsData?.rooms || [],
      }
    })

    console.log("user trips with rooms", {
      userId,
      isSuperAdmin,
      trips: tripsWithRooms,
    })

    return tripsWithRooms as ITrip[]
  } catch (err) {
    console.log("Getting trips error", err)
    return []
  }
}

export const createTrip = async (data: {
  name: string
  originPortGroups: SearchOrigin[]
  destinationRegions: SearchDestination[]
  months: number[]
  loggedInUser: {
    id: string
    firstName: string
    lastName: string
    referralCode: string
    avatar?: string
  }
  tags?: string[]
}) => {
  // reportLog("create trip")
  const { loggedInUser, name, months, destinationRegions, originPortGroups, tags } = data

  try {
    const tripData: TripData = {
      name: name.toString(),
      months,
      destinationRegions: destinationRegions.map((destination) => ({
        id: destination.id,
        name: destination.name,
      })),
      originPortGroups: originPortGroups.map((origin) => ({
        id: origin.id,
        name: origin.name,
      })),
      ownerId: loggedInUser.id,
      collaboratorIds: [loggedInUser.id],
      collaborators: [
        {
          userId: loggedInUser.id,
          firstName: loggedInUser.firstName,
          lastName: loggedInUser.lastName,
          avatar: loggedInUser.avatar,
          type: "agent",
        },
      ],
      tags: tags || [],
      isNavigatorLoading: false,
      created: Date.now(),
      updated: Date.now(),
      updatedBy: loggedInUser.id,
      bookingEnabled: false,
      loyaltyProgramEnabled: false,
    }

    const response = await callFirebaseFunction({
      name: "createTrip",
      payload: { tripData, autoJoinRoom: false },
      options: {
        timeoutSeconds: 60 * 15,
      },
    })

    Analytics.logTripCreation({
      name,
      destinationRegions: destinationRegions.map((region) => region.name).join(", "),
      originPortGroups: originPortGroups.map((region) => region.name).join(", "),
      months,
    })

    if (response.error) {
      // Alert.alert(`${_t("errors.createTrip")} ${_t("errors.tryAgainLater")}`)
      return null
    }

    const trip = TripModel.create(response.data.result)

    // logger.info("Trip has been created", trip.id)

    return trip
  } catch (err) {
    // Alert.alert(`${_t("errors.createTrip")} ${_t("errors.tryAgainLater")}`)
    // logger.error("create user trip error", err)
    console.log("create user trip error", err)

    return null
  }
}

export const updateTripSearch = async (
  tripId: TripID,
  tripSearchId: TripSearchID,
  data: Partial<ITripSearch>,
) => {
  const mainTripSearchDocRef = doc(firestore, `Trips/${tripId}/Searches/${tripSearchId}`)

  const mainTripSearchDocSnapshot = await getDoc(mainTripSearchDocRef)
  if (mainTripSearchDocSnapshot.exists()) {
    await updateDoc(mainTripSearchDocRef, data)
  }
}

export const updateTrip = async (tripId: string, data: Partial<ITrip>) => {
  try {
    const docRef = doc(firestore, `Trips/${tripId}`)
    const docSnapshot = await getDoc(docRef)
    const docData = docSnapshot.data() as Omit<ITrip, "id">

    await updateDoc(docRef, {
      ...data,
      updated: Date.now(),
    })

    const searchCriteriaProps: (keyof ITrip)[] = [
      "originPorts",
      "originPortGroups",
      "destinationRegions",
      "destinationPorts",
      "months",
    ]

    const searchCriteriaChanged = searchCriteriaProps.reduce((changed, propName) => {
      if (changed || data[propName] === undefined) {
        return changed
      }

      return !isEqual(docData[propName], data[propName])
    }, false)

    if (searchCriteriaChanged) {
      await updateTripSearch(tripId, "main", { status: "waiting" })
    }
  } catch (err) {
    console.error("update user trip error", err)
  }
}
